import { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../auth/components/AuthProvider';

const DEFAULT_METRICS = {
  solicitudesPendientes: 0,
  solicitudesConMatch: 0,
  empresasPorVerificar: 0,
  clientesNuevosMes: 0,
  consultasMes: 0,
  solicitudesAccesoPendientes: 0,
  updatedAt: null
};

const FALLBACK_LIMIT = 200;

/**
 * Fallback: queries limitadas cuando no existe admin_metrics/admin_dashboard.
 */
async function fetchFallbackMetrics() {
  const out = { ...DEFAULT_METRICS };
  try {
    const [snapIngresada, snapPend] = await Promise.all([
      getDocs(query(collection(db, 'solicitudes_empresa'), where('estado', '==', 'ingresada'), limit(FALLBACK_LIMIT))),
      getDocs(query(collection(db, 'solicitudes_empresa'), where('estado', '==', 'pendiente'), limit(FALLBACK_LIMIT)))
    ]);
    out.solicitudesPendientes = snapIngresada.size + snapPend.size;
  } catch (_) { /* sin índice o permisos */ }
  try {
    const qMatch = query(
      collection(db, 'solicitudes_empresa'),
      where('matchScore', '>=', 0.7),
      limit(FALLBACK_LIMIT)
    );
    const snapMatch = await getDocs(qMatch);
    out.solicitudesConMatch = snapMatch.size;
  } catch (_) { /* matchScore puede no estar indexado */ }
  try {
    const estadosVerificar = ['pendiente_validacion', 'correo_enviado', 'en_visita'];
    let total = 0;
    for (const est of estadosVerificar) {
      const q = query(
        collection(db, 'empresas'),
        where('estado', '==', est),
        limit(Math.ceil(FALLBACK_LIMIT / estadosVerificar.length))
      );
      const snap = await getDocs(q);
      total += snap.size;
    }
    out.empresasPorVerificar = total;
  } catch (_) { /* puede no haber campo estado en empresas */ }
  try {
    const snapAcceso = await getDocs(
      query(collection(db, 'solicitudes_acceso'), where('estado', '==', 'pendiente'), limit(FALLBACK_LIMIT))
    );
    out.solicitudesAccesoPendientes = snapAcceso.size;
  } catch (_) { /* sin permisos o colección vacía */ }
  return out;
}

/**
 * Lee admin_metrics/admin_dashboard para el tablero admin.
 * Fallback: si el doc no existe o falla, ejecuta queries limitadas para obtener métricas básicas.
 */
export function useAdminDashboardMetrics() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(DEFAULT_METRICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setMetrics(DEFAULT_METRICS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const ref = doc(db, 'admin_metrics', 'admin_dashboard');

    getDoc(ref)
      .then(async (snap) => {
        if (cancelled) return;
        if (snap.exists()) {
          const d = snap.data();
          setMetrics({
            solicitudesPendientes: typeof d.solicitudesPendientes === 'number' ? d.solicitudesPendientes : 0,
            solicitudesConMatch: typeof d.solicitudesConMatch === 'number' ? d.solicitudesConMatch : 0,
            empresasPorVerificar: typeof d.empresasPorVerificar === 'number' ? d.empresasPorVerificar : 0,
            clientesNuevosMes: typeof d.clientesNuevosMes === 'number' ? d.clientesNuevosMes : 0,
            consultasMes: typeof d.consultasMes === 'number' ? d.consultasMes : 0,
            solicitudesAccesoPendientes: typeof d.solicitudesAccesoPendientes === 'number' ? d.solicitudesAccesoPendientes : 0,
            updatedAt: d.updatedAt || null
          });
          return;
        }
        const fallback = await fetchFallbackMetrics();
        if (!cancelled) setMetrics(fallback);
      })
      .catch(async (err) => {
        if (cancelled) return;
        setError(err);
        const fallback = await fetchFallbackMetrics();
        if (!cancelled) setMetrics(fallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user]);

  return { metrics, loading, error };
}
